<template>
    <v-dialog
        v-if="imageObject"
        :model-value="modelValue"
        width="100%"
        height="100%"
        :fullscreen="true"
        content-class="products-image-modal bg-surface"
        @update:modelValue="onClose"
    >
        <v-btn
            class="dialog-close cursor-pointer"
            variant="plain"
            @click="onClose()"
            icon
        >
            <v-icon
                color="white"
                size="x-large"
                :icon="mdiCloseCircle"
            ></v-icon>
        </v-btn>
        
        <!-- Main -->
        <div
            class="
                products-image-modal-container
                d-flex
                flex-column
                flex-grow-1
                w-100
                h-100
            "
            v-resize="setWidth"
        >
            <div
                class="
                    flex-shrink-0
                    flex-grow-1
                "
            >
                <div
                    ref="elProdImgModalOuter"
                    class="prod-img-modal-outer carousel-h-scroll-parent w-100 h-100"
                    :class="{
                        'overflow-hidden': isZoomed
                    }"
                >
                    <div
                        class="prod-img-modal-inner h-100 fit-w-max-content d-flex"
                    >
                        <div
                            v-for="(img, index) in productImages"
                            ref="elProdImgModalItems"
                            :id="`prod-img-${img.uid}`"
                            class="prod-img-wrapper carousel-h-scroll-item"
                            :style="{ width: modalWidth }"
                            :data-attr-val="img.uid"
                        >
                            <div
                                class="w-100 h-100 position-relative"
                            >
                                <ProductsImageScalable
                                    v-if="img.url"
                                    :key="`prod-img-id-${img.uid}`"
                                    class="absolute-left"
                                    :style="{ width: modalWidth }"
                                    :alt="img?.alt"
                                    :image-object="img"
                                    :image-stack="imageStack"
                                    :type="['xx-large', 'x-large', 'large']"
                                ></ProductsImageScalable>
                                <template
                                    v-if="imageStack?.size && img.groupKey && imageStack.has(img.groupKey)"
                                >
                                    <ProductsImageScalable
                                        v-for="(item, index) in imageStack.get(img.groupKey)"
                                        :key="`prod-img-id-stack-${item.uid}-${index}`"
                                        class="absolute-left"
                                        :style="{ width: modalWidth }"
                                        :alt="item?.alt"
                                        :image-object="item"
                                        :type="['xx-large', 'x-large', 'large']"
                                    ></ProductsImageScalable>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Thumbs -->
            <div
                class="
                    pa-4
                    d-flex
                    flex-shrink-0
                    flex-grow-0
                    justify-center
                    no-user-select
                "
            >
                <ProductMainImageThumbs
                    :key="`prod-image-zoom-${currentSkuUid}`"
                    :product-images="productImages"
                    :selected-image-uid="currentSkuUid"
                    item-class="carousel-v-scroll-item"
                    :type="isMobile ? 'dots' : 'thumbs'"
                    :image-stack="imageStack"
                    orientation="horizontal"
                    selector-prefix="#prod-img"
                    :disabled="isZoomed"
                    @on-change="onImgChange"
                ></ProductMainImageThumbs>
            </div>
        </div>
        <!-- <v-chip>Drag &bull; Pinch &bull; Scroll</v-chip> -->
    </v-dialog>
</template>

<script lang="ts" setup>
    import type {
        FullImage,
        FullImageStack
    } from '~/types';
    import { mdiCloseCircle } from '@mdi/js';
    import { useIsMobile } from '~/composables';
    import ProductsImageScalable from '~/components/products/ProductsImageScalable/ProductsImageScalable.vue';
    import ProductMainImageThumbs from '~/components/products/ProductMainImageThumbs/ProductMainImageThumbs.vue';
    import { useIntersectionObserver } from '@vueuse/core';

    const props = defineProps<{
        modelValue: boolean;
        selectedImageUid: Nullable<string>;
        imageObject?: Nilish<FullImage>;
        productImages: Nullable<FullImage[]>;
        itemClass?: Nilish<string>;
        imageStack?: Nilish<FullImageStack>;
        type: 'thumbs' | 'dots';
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
        'on-exit': [value: void];
    }>();

    const isZoomed = ref(false);
    const currentSkuUid = ref<Nullable<string>>(null);
    const imgObj = ref(props.imageObject);
    const { isMobile } = useIsMobile();
    const elProdImgModalOuter = shallowRef<HTMLElement>();
    const elProdImgModalItems = ref<HTMLElement[]>([]);
    const modalWidth = ref<string>('auto');


    const {
        stop: itemsObsStop
    } = useIntersectionObserver(
        elProdImgModalItems,
        onIntersect,
        {
            root: elProdImgModalOuter,
            immediate: true,
            threshold: 0.1
        }
    );

    function onIntersect(entries: IntersectionObserverEntry[]): void {
        const entry = entries[0];
        if (entry && entry.target instanceof HTMLElement && entry.isIntersecting) {
            const val = entry.target.dataset['attrVal'] ?? null;
            currentSkuUid.value = val;
        }
    }

    const unWatchSelectedSku = watch(
        () => props.selectedImageUid,
        (newVal) => {
            currentSkuUid.value = newVal;
        },
        {
            immediate: true
        }
    );

    const unWatchImgObjProp = watch(
        () => props.imageObject,
        (newVal) => {
            imgObj.value = newVal;
        },
        {
            immediate: true
        }
    );

    function setWidth(): void {
        const w: string | number = elProdImgModalOuter.value?.clientWidth ?? '0';
        modalWidth.value = `${w}px`;
    }

    function onImgChange(imgIdSel: string): void {
        const target = elProdImgModalOuter.value?.querySelector(imgIdSel);

        if (!target || !(target instanceof HTMLElement)) {
            return;
        }

        currentSkuUid.value = target.dataset['attrVal'] ?? null;

        target.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'nearest'
        });
    }

    function onClose() {
        emit('update:modelValue', false);
        emit('on-exit');
    }

    onUpdated(() => {
        if (props.modelValue && currentSkuUid.value) {
            nextTick(() => {
                onImgChange(`#prod-img-${currentSkuUid.value}`);
            });
        }
    });

    onBeforeUnmount(() => {
        unWatchSelectedSku();
        unWatchImgObjProp();
        itemsObsStop();
    });
</script>

<style lang="scss">
.products-image-modal {
    position: relative;

    .dialog-close {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        filter: invert(1)
    }

    // .v-chip {
    //     position: absolute;
    //     left: 0;
    //     right: 0;
    //     margin: auto;
    //     width: fit-content;
    //     bottom: 1rem;
    //     filter: invert(1);
    //     mix-blend-mode: difference;
    // }
}
</style>
